import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { usePixelConfig } from '@/hooks/usePixelConfig';
import {
  trackPageView,
  initializeFBC,
  refreshFBCookies,
  getAdvancedMatchingData,
  generateEventId,
  setGeoCache,
} from '@/lib/facebookPixel';
import { trackGA4PageView } from '@/lib/googleAnalytics';

/**
 * Wait for the real SDK _fbp cookie to appear after fbq('init').
 * Polls every 50ms for up to maxWaitMs. Returns true if real _fbp was found.
 */
function waitForRealFBP(maxWaitMs = 800): Promise<boolean> {
  return new Promise(resolve => {
    const start = Date.now();
    const check = () => {
      // Check for _fbp cookie (set by fbevents.js SDK after init)
      const fbpCookie = document.cookie.split('; ').find(c => c.startsWith('_fbp='));
      if (fbpCookie) {
        resolve(true);
        return;
      }
      if (Date.now() - start >= maxWaitMs) {
        resolve(false);
        return;
      }
      setTimeout(check, 50);
    };
    check();
  });
}

// Module-level state — survives component remounts (prevents duplicate PageView)
let moduleInitialized = false;
let moduleLastPageViewPath: string | null = null;

export function FacebookPixel() {
  const location = useLocation();
  const pixelIdRef = useRef<string | null>(null);
  const config = usePixelConfig();

  // CRITICAL: Capture fbclid IMMEDIATELY on mount — before config loads
  // If we wait for config.pixelId (1-2s), the user may navigate and fbclid is lost
  useEffect(() => {
    initializeFBC();
  }, []);

  // Periodic FBC/FBP refresh to prevent mid-session cookie loss
  // (ad blockers or ITP may clear cookies during long browsing sessions)
  useEffect(() => {
    const interval = setInterval(() => {
      initializeFBC();
      refreshFBCookies();
    }, 5 * 60 * 1000); // every 5 minutes
    return () => clearInterval(interval);
  }, []);

  // IPv6 detection moved to index.html inline script for earlier execution.
  // Stage 3 (server-side fallback from get-pixel-config) remains below.

  // Improvement 3.5: Server-side IP is PRIMARY source (more authoritative than client-side)
  // Server sees the actual HTTP connection IP, while client-side APIs can be blocked
  useEffect(() => {
    if (!config.clientIpV6 && !config.clientIpV4) return;

    // Bug 3.3: Basic IP format validation
    const isValidIPv6 = (ip: string) => /^[0-9a-fA-F:]+$/.test(ip) && ip.includes(':');
    const isValidIPv4 = (ip: string) => /^\d{1,3}(\.\d{1,3}){3}$/.test(ip);

    // Server IPv6 OVERWRITES client-detected IP (it's more authoritative)
    if (config.clientIpV6 && isValidIPv6(config.clientIpV6)) {
      localStorage.setItem('_client_ip_v6', config.clientIpV6);
      localStorage.setItem('_client_ip_v6_data', JSON.stringify({ ip: config.clientIpV6, ts: Date.now() }));
      if (import.meta.env.DEV) console.log('IPv6 from server (primary):', config.clientIpV6);
    }
    // Server IPv4 ALWAYS overwrites client-detected IPv4 (server is more authoritative
    // than client-side services which can be blocked by ad blockers or return CDN proxy IPs)
    if (config.clientIpV4 && isValidIPv4(config.clientIpV4)) {
      localStorage.setItem('_client_ip', config.clientIpV4);
    }
  }, [config.clientIpV6, config.clientIpV4]);

  // Store server-detected country code (from Cloudflare CF-IPCountry header)
  // This is the most reliable country detection — based on actual IP geolocation,
  // not browser timezone which can be wrong (especially in Facebook in-app browser)
  useEffect(() => {
    if (config.countryCode) {
      localStorage.setItem('_server_country_code', config.countryCode.toUpperCase());
    }
  }, [config.countryCode]);

  // Store IP-based geolocation data for EMQ enrichment (city/state/zip)
  // These flow into buildBaseUserData() → CAPI edge function → hashed for Meta
  // setGeoCache() populates module-level cache instantly (fixes race condition where
  // first event fires before localStorage is written by get-pixel-config response)
  useEffect(() => {
    if (config.geoCity || config.geoRegion || config.geoPostal) {
      setGeoCache(config.geoCity, config.geoRegion, config.geoPostal);
    }
    if (config.geoCity) localStorage.setItem('_geo_city', config.geoCity);
    if (config.geoRegion) localStorage.setItem('_geo_region', config.geoRegion);
    if (config.geoPostal) localStorage.setItem('_geo_postal', config.geoPostal);
  }, [config.geoCity, config.geoRegion, config.geoPostal]);

  // Store calling code and privacy flags from iplocate.io
  useEffect(() => {
    if (config.callingCode) localStorage.setItem('_calling_code', config.callingCode);
    // Privacy flags — stored for passing to CAPI edge function
    const isSuspicious = config.isVPN || config.isProxy || config.isTor || config.isHosting || config.isAbuser || config.isAnonymous || config.isBogon;
    localStorage.setItem('_is_vpn', config.isVPN ? '1' : '0');
    localStorage.setItem('_is_suspicious', isSuspicious ? '1' : '0');
    localStorage.setItem('_is_abuser', config.isAbuser ? '1' : '0');
    localStorage.setItem('_is_anonymous', config.isAnonymous ? '1' : '0');
    localStorage.setItem('_is_bogon', config.isBogon ? '1' : '0');
    localStorage.setItem('_is_icloud_relay', config.isICloudRelay ? '1' : '0');
    if (config.asnType) localStorage.setItem('_asn_type', config.asnType);
    if (config.asnName) localStorage.setItem('_asn_name', config.asnName);
  }, [config.callingCode, config.isVPN, config.isProxy, config.isTor, config.isHosting, config.isAbuser, config.isAnonymous, config.isBogon, config.isICloudRelay, config.asnType, config.asnName]);

  // Initialize pixel when config is available
  useEffect(() => {
    async function initPixel() {
      if (moduleInitialized || !config.pixelId) return;

      try {
        // One-time cleanup: remove synthetic _fbp cookie so SDK can set its own
        try {
          const syntheticFbp = localStorage.getItem('_fb_synthetic_fbp');
          const currentCookieFbp = document.cookie.match(/_fbp=([^;]+)/)?.[1];
          if (syntheticFbp && currentCookieFbp === syntheticFbp) {
            const domain = window.location.hostname.split('.').slice(-2).join('.');
            document.cookie = `_fbp=; path=/; max-age=0; domain=.${domain}`;
          }
        } catch { /* silent */ }

        // Initialize FBC from URL parameter if present
        initializeFBC();

        const pixelId = config.pixelId;
        pixelIdRef.current = pixelId;

        // Store pixelId so reinitPixelWithPII() can re-init from anywhere
        try { localStorage.setItem('_fb_pixel_id', pixelId); } catch { /* silent */ }

        // Initialize Facebook Pixel with Advanced Matching
        if (window.fbq) {
          // If pre-initialized by index.html cache, skip redundant init
          // but still re-init with full advanced matching data
          const advancedMatchingData = getAdvancedMatchingData();

          if (window.__FB_PRE_INITIALIZED__) {
            // Already init'd in index.html with basic data — re-init with full matching
            window.fbq('init', pixelId, advancedMatchingData);
            if (import.meta.env.DEV) console.log('Facebook Pixel re-initialized with full Advanced Matching (was pre-init)');
          } else {
            window.fbq('init', pixelId, advancedMatchingData);
            if (import.meta.env.DEV) console.log('Facebook Pixel initialized with Advanced Matching:', pixelId, advancedMatchingData);
          }

          moduleInitialized = true;

          // Wait for SDK to set real _fbp cookie before firing first PageView.
          // 1500ms covers slow connections, mobile, and distant CDNs.
          const hasRealFBP = await waitForRealFBP(1500);

          if (hasRealFBP) {
            // SDK wrote its own real _fbp — check if it differs from our synthetic placeholder
            try {
              const realFbp = document.cookie.match(/_fbp=([^;]+)/)?.[1];
              const syntheticFbp = localStorage.getItem('_fb_synthetic_fbp');
              if (realFbp && syntheticFbp && realFbp !== syntheticFbp) {
                // SDK has its own value — remove the synthetic cookie so SDK's value wins.
                // Keep localStorage reference so we can detect future conflicts.
                const domain = window.location.hostname.split('.').slice(-2).join('.');
                const isPublicSuffix = /\.(app|dev|github\.io|netlify\.app|vercel\.app|pages\.dev)$/i.test(domain);
                if (!isPublicSuffix) {
                  document.cookie = `_fbp=; path=/; max-age=0; domain=.${domain}`;
                }
                document.cookie = `_fbp=; path=/; max-age=0`;
                // Give SDK 50ms to re-set its real _fbp after we cleared the synthetic
                await new Promise(resolve => setTimeout(resolve, 50));
                if (import.meta.env.DEV) console.log('Cleared synthetic _fbp, real SDK value wins:', realFbp);
              } else if (realFbp) {
                // SDK confirmed same value — safe to clear localStorage reference
                localStorage.removeItem('_fb_synthetic_fbp');
              }
            } catch { /* silent */ }
          }

          // Refresh cookies to sync SDK-set values to localStorage
          refreshFBCookies();

          // Fire first PageView for landing page
          moduleLastPageViewPath = location.pathname;
          // Deterministic PageView event_id: external_id + path + 10-min bucket.
          // Prevents duplicate PageViews from React StrictMode remounts and fast
          // refreshes while still creating fresh IDs across real visits.
          const bucket = Math.floor(Date.now() / 600000);
          const visitorId = (typeof localStorage !== 'undefined' ? localStorage.getItem('_fb_external_id') : '') || 'anon';
          const pvSeed = `PageView_${visitorId}_${location.pathname}_${bucket}`;
          const pvBuf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(pvSeed));
          const pvEventId = Array.from(new Uint8Array(pvBuf)).map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 36);
          await trackPageView(pvEventId);
          trackGA4PageView(pvEventId);

          // Delayed re-capture: Some browsers set _fbp slightly later
          setTimeout(() => {
            initializeFBC();
            refreshFBCookies();

            // Clean up synthetic FBP once SDK has set its own real cookie
            try {
              const realFbp = document.cookie.match(/_fbp=([^;]+)/)?.[1];
              const syntheticFbp = localStorage.getItem('_fb_synthetic_fbp');
              if (realFbp && syntheticFbp && realFbp !== syntheticFbp) {
                localStorage.removeItem('_fb_synthetic_fbp');
              }
            } catch { /* silent */ }

            if (import.meta.env.DEV) console.log('FB cookies refreshed after delay');
          }, 2000);
        } else {
          console.warn('fbq not loaded - pixel script may be blocked');
        }
      } catch (err) {
        console.error('Error initializing Facebook Pixel:', err);
      }
    }

    initPixel();
  }, [config.pixelId, config.clientIp, config.clientIpV6]);

  // Track page views on route changes + re-capture fbclid
  // Skips if init already fired PageView for this same path (dedup by path)
  useEffect(() => {
    if (!moduleInitialized) return;
    if (moduleLastPageViewPath === location.pathname) return;

    moduleLastPageViewPath = location.pathname;
    initializeFBC();
    refreshFBCookies();

    const timeout = setTimeout(async () => {
      // Deterministic event_id (visitor + path + 10-min bucket) — collapses
      // duplicate PageViews from StrictMode/HMR while still creating fresh IDs
      // across real visits. Shared between FB and GA4 for dashboard dedup.
      const bucket = Math.floor(Date.now() / 600000);
      const visitorId = (typeof localStorage !== 'undefined' ? localStorage.getItem('_fb_external_id') : '') || 'anon';
      const seed = `PageView_${visitorId}_${location.pathname}_${bucket}`;
      const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(seed));
      const pvEventId = Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 36);
      await Promise.all([
        trackPageView(pvEventId),
        trackGA4PageView(pvEventId),
      ]);
    }, 100);

    return () => clearTimeout(timeout);
  }, [location.pathname]);

  return null;
}
